import {Component, Inject, OnInit} from '@angular/core';
import {SeasonDetailFieldModel} from "../../models/season-models/season-detail-field-model";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {SeasonDropdownModel} from "../../models/season-models/season-dropdown-model";
import {data} from 'autoprefixer';
import { CreateEditSeasonComponent } from '../season-manager-dialogs/create-edit-season/create-edit-season.component';
import { SeasonManagerService } from '../../services/season-manager.service';
import { SeasonSharedDataService } from '../../services/season-shared-data.service';
import { TenantService } from '../../services/tenant.service';
import { Season } from 'src/app/models/season';
import { SeasonFieldBase } from '../../dto/season-dto/season-field-base';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DraftConfirmationDialogComponent } from '../draft-confirmation-dialog/draft-confirmation-dialog.component';

@Component({
    selector: 'app-field-details',
    templateUrl: './field-details.component.html',
    styleUrls: ['./field-details.component.css'],
    standalone: false
})
export class FieldDetailsComponent implements OnInit {

  seasonFormData: SeasonDetailFieldModel = null;
  seasons: SeasonDropdownModel[] = [];
  fieldId: number = 0;
  sharedExtraDetailData: any = null;
  selectedDefinitionIndex: number = 0;

  /**
   * Mirrors selectedDefinitionIndex, but is only updated once a tab change is
   * accepted. The template's [(selectedIndex)] two-way binding moves
   * selectedDefinitionIndex before (selectedIndexChange) fires, so this is the
   * only reliable reference to the definition being left.
   */
  private previousDefinitionIndex: number = 0;
  private isTabSwitching: boolean = false;
  panelTab: 'field' | 'crop' | 'cost' = 'field';
  private hasSaved = false;

  get currentDefinition(): SeasonFieldBase {
    if (this.seasonFormData?.details?.length > 0) {
      return this.seasonFormData.details[this.selectedDefinitionIndex] || this.seasonFormData.detail;
    }
    return this.seasonFormData?.detail;
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
              public dialogRef: MatDialogRef<SeasonDetailFieldModel>,
              public createSeasonDialog: MatDialog,
              private seasonManagerService: SeasonManagerService,
              private seasonSharedDataService: SeasonSharedDataService,
              private tenantService: TenantService,
              private snackBar: MatSnackBar,
            ) {

  }

  ngOnInit() {
    this.fieldId = this.data.fieldId;
    this.seasonFormData = {...this.data.seasonFormData};
    // Ensure details array exists, use detail as first entry (it has merged extra data from parent)
    if (!this.seasonFormData.details || this.seasonFormData.details.length === 0) {
      this.seasonFormData.details = this.seasonFormData.detail ? [this.seasonFormData.detail] : [];
    } else {
      // Replace first entry with detail (which has extra data merged by parent service)
      this.seasonFormData.details[0] = this.seasonFormData.detail;
    }

    // Resolve active definition index
    const activeIdx = this.seasonFormData.details.findIndex((d: any) => d.isSelectedCropDefinition === true || d.isActive === true);
    this.selectedDefinitionIndex = activeIdx > -1 ? activeIdx : 0;
    this.previousDefinitionIndex = this.selectedDefinitionIndex;
    this.seasons = this.data.seasons;
    // Use parent's pre-fetched extra detail for initial definition
    const detail = this.seasonFormData.detail;
    this.sharedExtraDetailData = {
      grainFeed: detail?.grainFeed,
      vegetables: detail?.vegetables,
      vineyard: detail?.vineyard,
      fruit: detail?.fruit,
    };

    // If active definition is not index 0, fetch its correct extra detail
    if (this.selectedDefinitionIndex !== 0) {
      this.loadExtraDetailForCurrentDefinition();
    }
  }

  isReady = true;

  loadExtraDetailForCurrentDefinition() {
    const def = this.currentDefinition;
    if (!def?.id || !def?.fieldType) {
      this.sharedExtraDetailData = {};
      return;
    }
    const fieldTypeId = typeof def.fieldType === 'object' ? (def.fieldType as any).id : Number(def.fieldType);
    if (isNaN(fieldTypeId)) {
      this.sharedExtraDetailData = {};
      return;
    }
    this.seasonManagerService.getSeasonDetailExtraData(def.id, fieldTypeId).subscribe((response) => {
      this.sharedExtraDetailData = response || {};
    });
  }

  updateExtraDetailData() {
    this.loadExtraDetailForCurrentDefinition();
  }

  onDefinitionTabChange(index: number) {
    // The template binds [(selectedIndex)] AND (selectedIndexChange), so the
    // two-way assignment has already moved selectedDefinitionIndex to the new
    // tab before this runs. previousDefinitionIndex is therefore the only
    // reliable reference to the definition the user was actually editing.
    const previousIndex = this.previousDefinitionIndex;

    // Tabs are disabled while the cultivar size is invalid; this guard is the
    // backstop so the silent auto-save below can never persist a bad value.
    if (this.cultivarSizeInvalid && index !== previousIndex) {
      // The binding already moved the tab, so put it back.
      setTimeout(() => this.selectedDefinitionIndex = previousIndex);
      return;
    }

    // Auto-save the definition being left — not the one being switched to.
    this.saveCurrentDefinitionSilently(previousIndex, () => {
      this.loadExtraDetailForCurrentDefinition();
    });
    this.isTabSwitching = true;
    this.selectedDefinitionIndex = index;
    this.previousDefinitionIndex = index;
    // Update isSelectedCropDefinition flags in-memory
    const defs = this.seasonFormData?.details || [];
    defs.forEach((d: any, i: number) => d.isSelectedCropDefinition = (i === index));
    // Mark new definition as selected and persist
    const newDef = this.seasonFormData?.details?.[index];
    if (newDef?.id) {
      this.seasonManagerService.updateSeasonFieldBaseDetails(newDef.id, { isSelectedCropDefinition: true }).subscribe();
    }
    setTimeout(() => this.isTabSwitching = false, 0);
  }

  private saveCurrentDefinitionSilently(definitionIndex: number, onComplete?: () => void) {
    const def: any = this.seasonFormData?.details?.[definitionIndex];
    if (!def?.id) { if (onComplete) onComplete(); return; }

    const fieldTypeId = def?.fieldTypeId ?? def?.fieldType;
    const seasonDetails = {
      ...def,
      isSelectedCropDefinition: false,
      fieldTypeId: fieldTypeId,
      currentCropId: def?.currentCropId ?? def?.currentCrop,
      irrigationTypeId: def?.irrigationTypeId ?? def?.irrigationType,
    };
    delete seasonDetails.fieldId;
    delete seasonDetails.id;
    delete seasonDetails.grainFeed;
    delete seasonDetails.vegetables;
    delete seasonDetails.vineyard;
    delete seasonDetails.fruit;

    let pendingSaves = 1; // base save always counts
    const checkComplete = () => { pendingSaves--; if (pendingSaves === 0 && onComplete) onComplete(); };

    // Count crop detail save if applicable
    if ((fieldTypeId == 1 && def.grainFeed) || (fieldTypeId == 2 && def.vegetables) ||
        (fieldTypeId == 3 && def.vineyard) || (fieldTypeId == 4 && def.fruit)) {
      pendingSaves++;
    }

    this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, seasonDetails).subscribe(
      () => checkComplete(), () => checkComplete()
    );

    if (fieldTypeId == 1 && def.grainFeed) {
      this.seasonManagerService.postSeasonDetailsGrainFeed(def.id, def.grainFeed).subscribe(
        () => checkComplete(), () => checkComplete()
      );
    }
    if (fieldTypeId == 2 && def.vegetables) {
      this.seasonManagerService.postSeasonDetailsVegetables(def.id, def.vegetables).subscribe(
        () => checkComplete(), () => checkComplete()
      );
    }
    if (fieldTypeId == 3 && def.vineyard) {
      this.seasonManagerService.postSeasonDetailsVineyard(def.id, def.vineyard).subscribe(
        () => checkComplete(), () => checkComplete()
      );
    }
    if (fieldTypeId == 4 && def.fruit) {
      this.seasonManagerService.postSeasonDetailsFruit(def.id, def.fruit).subscribe(
        () => checkComplete(), () => checkComplete()
      );
    }
  }

  getDefinitionLabel(def: SeasonFieldBase, index: number): string {
    return def?.cultivar || `Definition ${index + 1}`;
  }

  addDefinition() {
    const seasonId = this.seasonFormData.season?.id;
    if (!seasonId) return;

    const dialogRef = this.createSeasonDialog.open(DraftConfirmationDialogComponent, {
      data: { dialogType: 'save', dialogTitle: 'Save Changes', dialogSubTitle: 'All current changes will be saved before creating a new definition.' }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.saveAllDefinitions(() => {
        const currentDef = this.seasonFormData.details[this.selectedDefinitionIndex];
        this.seasonManagerService.addDefinition(seasonId, this.fieldId, currentDef).subscribe((newDef) => {
          newDef.fieldName = this.seasonFormData.details[0]?.fieldName ?? this.seasonFormData.field?.name;
          newDef.allowNotifications = this.seasonFormData.details[0]?.allowNotifications;
          newDef.fieldSize = this.seasonFormData.details[0]?.fieldSize;
          if (typeof newDef.fieldType === 'object' && newDef.fieldType) newDef.fieldType = (newDef.fieldType as any).id;
          if (typeof newDef.currentCrop === 'object' && newDef.currentCrop) newDef.currentCrop = (newDef.currentCrop as any).id;
          if (typeof newDef.irrigationType === 'object' && newDef.irrigationType) newDef.irrigationType = (newDef.irrigationType as any).id;
          this.seasonFormData.details.push(newDef);
          this.selectedDefinitionIndex = this.seasonFormData.details.length - 1;
          this.previousDefinitionIndex = this.selectedDefinitionIndex;
          this.updateExtraDetailData();
          this.snackBar.open('Definition added', 'close', { duration: 3000 });
        });
      });
    });
  }

  /** Cultivar size rejection from the API, rendered on the input by the child. */
  cultivarSizeServerError: string = null;

  /**
   * True while the current definition's cultivar size would push the field over
   * its total. Blocks anything that would persist or navigate away from the
   * invalid value: definition tabs, panel tabs, Add Definition and Update
   * Details. Closing the dialog stays available - it discards rather than
   * saving, so it is a safe way out.
   */
  cultivarSizeInvalid: boolean = false;

  /**
   * Pulls the message out of a 400 from the cultivar size check.
   *
   * Read defensively: PG-358 throws a ResponseStatusException, giving Spring's
   * default body, but the architecture document specifies a structured
   * { error, message, details } shape. Handle either, and ignore any other
   * failure so unrelated errors are not shown against this field.
   */
  private extractCultivarSizeError(err: any): string | null {
    if (err?.status !== 400) return null;
    const body = err?.error;
    const message = typeof body === 'string' ? body : body?.message;
    if (typeof message === 'string' && message.toLowerCase().includes('cultivar size')) {
      return message;
    }
    return null;
  }

  private saveAllDefinitions(onComplete?: () => void) {
    const definitions = this.seasonFormData.details?.length > 0 ? this.seasonFormData.details : [];
    let remaining = definitions.length;
    if (remaining === 0) { if (onComplete) onComplete(); return; }

    definitions.forEach((def: any) => {
      if (!def?.id) { remaining--; if (remaining === 0 && onComplete) onComplete(); return; }
      const fieldTypeId = def?.fieldTypeId ?? def?.fieldType;
      const seasonDetails = {
        ...def,
        fieldTypeId: fieldTypeId,
        currentCropId: def?.currentCropId ?? def?.currentCrop,
        irrigationTypeId: def?.irrigationTypeId ?? def?.irrigationType,
      };
      ['fieldId', 'id', 'grainFeed', 'vegetables', 'vineyard', 'fruit'].forEach(key => delete seasonDetails[key]);

      this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, seasonDetails).subscribe(() => {
        this.cultivarSizeServerError = null;
        remaining--;
        if (remaining === 0 && onComplete) onComplete();
      }, (err: any) => {
        const message = this.extractCultivarSizeError(err);
        if (message) this.cultivarSizeServerError = message;
        remaining--;
        if (remaining === 0 && onComplete) onComplete();
      });

      // Save crop detail (extra details)
      if (fieldTypeId == 1 && def.grainFeed) {
        this.seasonManagerService.postSeasonDetailsGrainFeed(def.id, def.grainFeed).subscribe();
      }
      if (fieldTypeId == 2 && def.vegetables) {
        this.seasonManagerService.postSeasonDetailsVegetables(def.id, def.vegetables).subscribe();
      }
      if (fieldTypeId == 3 && def.vineyard) {
        this.seasonManagerService.postSeasonDetailsVineyard(def.id, def.vineyard).subscribe();
      }
      if (fieldTypeId == 4 && def.fruit) {
        this.seasonManagerService.postSeasonDetailsFruit(def.id, def.fruit).subscribe();
      }
    });
  }

  deleteDefinition(def: SeasonFieldBase, event: Event) {
    event.stopPropagation();
    if (this.seasonFormData.details.length <= 1) return;

    const dialogRef = this.createSeasonDialog.open(DraftConfirmationDialogComponent, {
      data: { dialogType: 'delete', dialogTitle: 'Delete Definition', dialogSubTitle: 'Are you sure you want to delete this crop definition?' }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.seasonManagerService.deleteDefinition(def.id).subscribe(() => {
        this.seasonFormData.details = this.seasonFormData.details.filter(d => d.id !== def.id);
        if (this.selectedDefinitionIndex >= this.seasonFormData.details.length) {
          this.selectedDefinitionIndex = this.seasonFormData.details.length - 1;
          this.previousDefinitionIndex = this.selectedDefinitionIndex;
        }
        this.seasonFormData.detail = this.seasonFormData.details[0];
        this.updateExtraDetailData();
        this.snackBar.open('Definition deleted', 'close', { duration: 3000 });
      });
    });
  }

  onFormDataChange(data: any) {
    // Track cultivar size validity regardless of the populate/tab-switch guards
    // below, so the navigation blocks stay accurate at all times.
    this.cultivarSizeInvalid = !!data?.name?.controls?.cultivarSize?.hasError('cultivarSizeSum');

    if (data && !data.isFormPopulating && !this.isTabSwitching) {
      if (this.seasonFormData.details?.length > 0) {
        const existing = this.seasonFormData.details[this.selectedDefinitionIndex];
        const updatedFieldName = data.value.fieldName ?? existing?.fieldName;
        const updated = {
          ...data.value,
          id: existing?.id,
          fieldId: existing?.fieldId,
          fieldName: updatedFieldName,
          fieldSize: existing?.fieldSize,
          fieldType: data.value.fieldTypeId ?? existing?.fieldType,
          currentCrop: data.value.currentCropId ?? existing?.currentCrop,
          irrigationType: data.value.irrigationTypeId ?? existing?.irrigationType,
        };
        this.seasonFormData.details[this.selectedDefinitionIndex] = updated;

        // fieldName is a field-level property — propagate to all definitions so
        // whichever definition saves last still writes the correct name.
        if (updatedFieldName != null) {
          this.seasonFormData.details.forEach((def: any, i: number) => {
            if (i !== this.selectedDefinitionIndex) {
              def.fieldName = updatedFieldName;
            }
          });
        }
      }
      this.seasonFormData.detail = this.seasonFormData.details[0];
    }
  }

  linkSeasonToField() {
    this.dialogRef.close('seasonLinked');
  }

 
 refreshSeasonsListAndSelect(newSeasonId: number) {
   const tenantId = sessionStorage.getItem('selectedTenant');
   
   if (tenantId) {
     this.seasonManagerService.getSeasonsListForDropDown(Number(tenantId)).subscribe((seasons) => {
       this.seasons = seasons;
       console.log('Refreshed seasons:', seasons);
       
       // Find the newly created season from the refreshed list
       const newSeason = seasons.find(s => s.id === newSeasonId);
       
       if (this.seasonFormData && newSeason) {
         console.log('Found new season:', newSeason);
         
         // Save to sessionStorage
         sessionStorage.setItem('lastCreatedSeason', JSON.stringify(newSeason));
         
         // Set it in the form data (cast to any to handle type mismatch)
         this.seasonFormData = {
           ...this.seasonFormData,
           season: newSeason as any
         };
         
         console.log('Updated seasonFormData:', this.seasonFormData.season);
       }
     });
   }
 }


  closeDialog(updateDetails?: boolean) {
    if (updateDetails) {
      // User explicitly saving on close
      if (this.seasonFormData.details?.length > 0) {
        this.seasonFormData.detail = this.seasonFormData.details[this.selectedDefinitionIndex];
      }
      this.dialogRef.close(this.seasonFormData);
    } else if (this.hasSaved) {
      // Already saved via Update Details — just signal refresh without re-saving
      this.dialogRef.close({ ...this.seasonFormData, _alreadySaved: true });
    } else {
      this.dialogRef.close(null);
    }
  }

  saveAndStayOpen() {
    // If no season linked, just save field name and notifications
    if (!this.seasonFormData.season) {
      const detail = this.seasonFormData.details?.[0] || this.seasonFormData.detail || {};
      const fieldName = detail.fieldName || this.seasonFormData.field?.name;
      const allowNotifications = detail.allowNotifications || false;
      this.seasonManagerService.updateFieldNameAllowNotification(this.fieldId, fieldName, allowNotifications)
        .subscribe({
          next: () => {
            if (this.seasonFormData.field) this.seasonFormData.field.name = fieldName;
            this.hasSaved = true;
            this.snackBar.open('Field details updated', 'close', { duration: 3000 });
          }
        });
      return;
    }

    this.saveAllDefinitions(() => {
      // Re-fetch definitions from API to prevent stale data (mirrors season-admin pattern)
      this.seasonManagerService.getDetailsOfFieldById(this.fieldId).subscribe((response: any) => {
        const freshDefs = response?.details?.length > 0 ? response.details : (response?.detail ? [response.detail] : []);
        if (freshDefs.length > 0) {
          this.seasonFormData.details = freshDefs;
          this.seasonFormData.detail = freshDefs[0];
          if (this.selectedDefinitionIndex >= freshDefs.length) {
            this.selectedDefinitionIndex = freshDefs.length - 1;
            this.previousDefinitionIndex = this.selectedDefinitionIndex;
          }
        }
        this.loadExtraDetailForCurrentDefinition();
        this.hasSaved = true;
        this.snackBar.open('Field details updated', 'close', { duration: 3000 });
      });
    });
  }

}
